import { useEffect } from "react";
import { events, pastEvents } from "@/data/athlete";
import { SectionHeader } from "./_shared";
import { SeniorSchedule } from "./SeniorSchedule";
import { MapPin, Radio, ChevronDown, Calendar, History } from "lucide-react";

export const Events = () => {
  // Auto-expand all event <details> for PDF/print, restore after
  useEffect(() => {
    const handleBeforePrint = () => {
      const all = document.querySelectorAll(
        '[data-testid="events-section"] details'
      );
      all.forEach((d) => {
        if (!d.hasAttribute("open")) d.setAttribute("data-was-closed", "true");
        d.setAttribute("open", "");
      });
    };
    const handleAfterPrint = () => {
      const all = document.querySelectorAll(
        '[data-testid="events-section"] details[data-was-closed]'
      );
      all.forEach((d) => {
        d.removeAttribute("open");
        d.removeAttribute("data-was-closed");
      });
    };
    window.addEventListener("beforeprint", handleBeforePrint);
    window.addEventListener("afterprint", handleAfterPrint);
    return () => {
      window.removeEventListener("beforeprint", handleBeforePrint);
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, []);

  return (
    <section
      id="events"
      data-testid="events-section"
      className="border-t border-zinc-900 py-16 md:py-24 bg-[#0c0c0c]"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <SectionHeader
          eyebrow="04 — Schedule"
          title="Upcoming Events"
          kicker="Season schedule, camps, showcases, and upcoming evaluation opportunities."
          testId="events-header"
        />

        <ul
          data-testid="events-list"
          className="divide-y divide-zinc-900 border-y border-zinc-900"
        >
          {events.length === 0 ? (
            <li
              data-testid="events-empty"
              className="py-8 text-center text-sm md:text-base italic text-zinc-500"
            >
              No upcoming events scheduled
            </li>
          ) : (
            events.map((e, i) => (
            <li
              key={`${e.name}-${i}`}
              data-testid={`event-item-${i}`}
              className="group"
            >
              <details className="group/details">
                <summary
                  data-testid={`event-summary-${i}`}
                  className="grid grid-cols-12 gap-3 md:gap-6 items-start md:items-center py-5 md:py-6 px-2 -mx-2 cursor-pointer list-none hover:bg-[#141414] transition-colors"
                >
                  {/* Date */}
                  <div className="col-span-3 md:col-span-2">
                    <div className="font-display text-3xl md:text-4xl text-white uppercase leading-none">
                      {e.date}
                    </div>
                    <div className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mt-1">
                      {e.year}
                    </div>
                  </div>

                  {/* Event meta */}
                  <div className="col-span-9 md:col-span-7">
                    <div className="text-[10px] md:text-xs uppercase tracking-[0.25em] font-bold text-[#FF3B30] mb-1">
                      {e.tag}
                    </div>
                    <div className="text-white font-bold text-base md:text-xl leading-snug">
                      {e.name}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-zinc-400 mt-1">
                      {e.location ? (
                        <span className="flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5" />
                          {e.location}
                        </span>
                      ) : e.seasonNote ? (
                        <span className="flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5 text-[#FF3B30]" />
                          {e.seasonNote}
                        </span>
                      ) : null}
                      {e.infoLink && (
                        <a
                          data-testid={`event-info-link-${i}`}
                          href={e.infoLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(ev) => ev.stopPropagation()}
                          className="inline-flex items-center gap-1.5 text-[#FF3B30] hover:text-[#FF5147] font-semibold transition-colors underline-offset-2 hover:underline"
                        >
                          <Radio className="h-3.5 w-3.5" />
                          Event Info
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Chevron */}
                  <div className="col-span-12 md:col-span-3 flex md:justify-end items-center">
                    <span className="inline-flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-[0.25em] font-bold text-zinc-500 group-hover/details:text-[#FF3B30] transition-colors">
                      Details
                      <ChevronDown className="h-4 w-4 transition-transform duration-200 group-open/details:rotate-180" />
                    </span>
                  </div>
                </summary>

                {/* Expanded panel */}
                <div
                  data-testid={`event-details-${i}`}
                  className="px-2 -mx-2 pb-6 md:pb-7"
                >
                  {e.seasonSchedule ? (
                    <SeniorSchedule />
                  ) : (
                  <div className="md:ml-[16.6667%] border-l-2 border-[#FF3B30] pl-5 md:pl-6 py-2 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 bg-[#0a0a0a]">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 mb-1">
                        Team
                      </div>
                      <div className="text-white text-sm md:text-base">
                        {e.team}
                        {e.circuit && (
                          <span className="block text-xs text-zinc-400 mt-0.5">
                            {e.circuit}
                          </span>
                        )}
                      </div>
                    </div>
                    {e.venue && (
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 mb-1">
                          Venue
                        </div>
                        <div className="text-white text-sm md:text-base leading-snug whitespace-pre-line">
                          {e.venue}
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 mb-1">
                        Game Schedule
                      </div>
                      <div className="text-white text-sm md:text-base flex items-start gap-2">
                        <Calendar className="h-4 w-4 text-[#FF3B30] mt-0.5 shrink-0" />
                        <span className="whitespace-pre-line">
                          {e.schedule || (
                            <span className="italic text-zinc-400">
                              Game times will be updated prior to event
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                  )}

                  {e.announcement && (
                    <div
                      data-testid={`event-announcement-${i}`}
                      className="md:ml-[16.6667%] mt-3 flex items-start gap-3 border border-[#FF3B30]/40 bg-[#1a0f0f] px-4 py-3"
                    >
                      <Radio className="h-4 w-4 text-[#FF3B30] mt-0.5 shrink-0" />
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF3B30] mb-0.5">
                          Event Update
                        </div>
                        <div className="text-white text-sm md:text-base leading-snug">
                          {e.announcement}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </details>
            </li>
          )))}
        </ul>

        {/* Past Events */}
        <div className="mt-16 md:mt-20" data-testid="past-events-block">
          <div className="mb-3 flex items-baseline gap-3 flex-wrap">
            <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF3B30]">
              Past Events
            </span>
            <span className="h-px flex-1 bg-zinc-800 min-w-[40px]" />
          </div>
          <p
            data-testid="past-events-subtitle"
            className="mb-8 text-sm md:text-base text-zinc-400"
          >
            Completed seasons, camps, showcases, and evaluation events.
          </p>

          {pastEvents.length === 0 ? (
            <p
              data-testid="past-events-empty"
              className="py-4 text-sm md:text-base italic text-zinc-500"
            >
              No past events on record
            </p>
          ) : (
            <ul
              data-testid="past-events-list"
              className="divide-y divide-zinc-900 border-y border-zinc-900"
            >
              {pastEvents.map((e, i) => (
                <li
                  key={`past-${e.name}-${i}`}
                  data-testid={`past-event-item-${i}`}
                  className="grid grid-cols-12 gap-3 md:gap-6 items-start py-5 md:py-6 px-2 -mx-2 opacity-80"
                >
                  <div className="col-span-3 md:col-span-2">
                    <div className="font-display text-2xl md:text-3xl text-white uppercase leading-none">
                      {e.date}
                    </div>
                    <div className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mt-1">
                      {e.year}
                    </div>
                  </div>
                  <div className="col-span-9 md:col-span-7">
                    <div className="text-[10px] md:text-xs uppercase tracking-[0.25em] font-bold text-zinc-500 mb-1 flex items-center gap-2">
                      <History className="h-3 w-3" />
                      {e.tag} · Completed
                    </div>
                    <div className="text-white font-bold text-base md:text-lg leading-snug">
                      {e.name}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-zinc-400 mt-1">
                      <span className="flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5" />
                        {e.location}
                      </span>
                      {e.venue && (
                        <span className="text-zinc-400">· {e.venue}</span>
                      )}
                    </div>
                    {e.schedule && (
                      <div className="mt-2 flex items-start gap-2 text-xs md:text-sm text-zinc-400">
                        <Calendar className="h-3.5 w-3.5 text-[#FF3B30] mt-0.5 shrink-0" />
                        <span className="whitespace-pre-line leading-snug">
                          {e.schedule}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="col-span-12 md:col-span-3 md:text-right text-xs uppercase tracking-[0.2em] text-zinc-600 font-semibold">
                    #{e.jersey} · {e.team}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};
